import { ChangeEvent, useState } from 'react';
import { Box, Button, Link, Select, Text } from '@nimbus-ds/components';
import ReactPortal from 'App/components/lab/ReactPortal/ReactPortal';
import { useTopAdBar } from 'App/components/TopAdBar/useTopAdBar';
import InputNumberNimbus from 'commons/components/InputNumberNimbus/InputNumberNimbus';
import { useResponsive } from 'commons/components/Responsive';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import { useFixedHeader } from '../../hooks/useFixedHeader';

export enum Action {
  NONE = 'none',
  TOP = 'top',
  BOTTOM = 'bottom',
  SPECIFIC = 'specific',
  GROUP = 'group',
}

type SimpleAction = {
  action: Action.NONE | Action.TOP | Action.BOTTOM | Action.GROUP;
};

type SpecificAction = {
  action: Action.SPECIFIC;
  position: number;
};

export type ActionSelectedParams = SimpleAction | SpecificAction;

interface SelectedProductsActionBarProps {
  count: number;
  productsCount: number;
  disabled: boolean;
  parentRef: React.RefObject<HTMLDivElement>;
  onActionSelected: (data: ActionSelectedParams) => Promise<void>;
  onUnselectAll: () => void;
}

function SelectedProductsActionBar({
  count,
  productsCount,
  disabled,
  parentRef,
  onActionSelected,
  onUnselectAll,
}: Readonly<SelectedProductsActionBarProps>) {
  const t = useTranslationCatalog('products.order.productsBySeccion');
  const { isMobile, isDesktop } = useResponsive();
  const { isShowTopAdBar, height: topAddBarHeight } = useTopAdBar();
  const [action, setAction] = useState<Action>(Action.NONE);
  const [position, setPosition] = useState<number | undefined>();
  const parentRect = parentRef.current?.getBoundingClientRect();
  const { top: rectTop = 0, left = 0, width = 0 } = parentRect ?? {};
  const top = rectTop + window.scrollY;
  const { isFixed } = useFixedHeader(top);

  if (count <= 0) return null;

  const handleChangeAction = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedAction = event.target.value as Action;
    if (selectedAction === Action.SPECIFIC) {
      setAction(Action.SPECIFIC);
      return;
    }
    setAction(Action.NONE);
    onActionSelected({ action: selectedAction });
  };

  const handleChangePosition = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!isNaN(value)) {
      setPosition(value);
    }
  };

  const handleMoveToSpecific = () => {
    if (action !== Action.SPECIFIC || position === undefined || position === 0)
      return;
    const positionToUse = position > productsCount ? productsCount : position;
    onActionSelected({
      action: Action.SPECIFIC,
      position: positionToUse,
    });
    setPosition(undefined);
    setAction(Action.NONE);
  };

  const handleUnselectAll = () => {
    onUnselectAll();
    setAction(Action.NONE);
    setPosition(undefined);
  };

  const unselectAll = (
    <Link
      as="button"
      appearance="primary"
      disabled={disabled}
      onClick={handleUnselectAll}
    >
      {t('unselectAll')}
    </Link>
  );

  const content = (
    <>
      <Text color="neutral-textLow">
        {t('selected', {
          count,
        })}
      </Text>
      {isMobile && (
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          {unselectAll}
        </Box>
      )}

      <div
        style={{
          ...(isMobile && {
            gridColumn: '1/3',
          }),
        }}
      >
        <Select
          id="action-select"
          aria-label={t('actions.moveTo')}
          name="action-select"
          value={action}
          onChange={handleChangeAction}
          disabled={disabled}
        >
          <Select.Option
            disabled
            label={t('actions.moveTo')}
            value={Action.NONE}
          />
          <Select.Option label={t('actions.toStart')} value={Action.TOP} />
          <Select.Option label={t('actions.toEnd')} value={Action.BOTTOM} />
          <Select.Option
            label={t('actions.toSpecific')}
            value={Action.SPECIFIC}
          />
        </Select>
      </div>

      {action === Action.SPECIFIC && (
        <div
          style={{
            display: 'grid',
            gap: '8px',
            gridTemplateColumns: '0.4fr 0.6fr 1fr',
            alignItems: 'center',
            ...(isMobile && {
              gridColumn: '1/3',
            }),
          }}
        >
          <InputNumberNimbus
            type="integer"
            aria-label={t('actions.toSpecific')}
            value={position}
            onChange={handleChangePosition}
            disabled={disabled}
          />
          <Text color="neutral-textLow">
            {t('actions.of', {
              count: productsCount,
            })}
          </Text>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            gap="2"
          >
            {isDesktop && unselectAll}
            <Button
              appearance="neutral"
              disabled={disabled}
              onClick={handleMoveToSpecific}
            >
              {t('actions.move')}
            </Button>
          </Box>
        </div>
      )}
      {action !== Action.SPECIFIC && isDesktop && (
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          {unselectAll}
        </Box>
      )}
    </>
  );

  // Common props for both action bar containers
  const commonContainerProps = {
    backgroundColor: 'neutral-surface' as const,
    paddingTop: '2' as const,
    paddingBottom: '2' as const,
    paddingLeft: '4' as const,
    paddingRight: '4' as const,
  };

  const commonInnerBoxProps = {
    alignItems: 'center' as const,
    gridTemplateColumns: {
      xs: '0.8fr 1.2fr',
      md: '0.5fr 0.8fr 1.7fr',
    } as const,
    gap: { xs: '2', md: '2' } as const,
  };

  const actionBarContentFixed = (
    <Box
      position="fixed"
      top={isShowTopAdBar && isDesktop ? `${topAddBarHeight}px` : '0'}
      left={isDesktop ? `${left}px` : 'inherit'}
      width="100%"
      zIndex="600"
      borderStyle="none"
      borderTopWidth="none"
      borderBottomWidth="none"
      borderColor="neutral-surfaceHighlight"
      {...commonContainerProps}
    >
      <Box
        display={isFixed ? 'grid' : 'none'}
        width={isDesktop ? `${width - 32}px` : '100%'}
        {...commonInnerBoxProps}
      >
        {content}
      </Box>
    </Box>
  );

  const actionBarContentStatic = (
    <Box
      position="relative"
      borderStyle="solid"
      borderTopWidth="1"
      borderBottomWidth="1"
      borderColor="neutral-surfaceHighlight"
      {...commonContainerProps}
    >
      <Box display="grid" width="100%" {...commonInnerBoxProps}>
        {content}
      </Box>
    </Box>
  );

  return (
    <>
      <ReactPortal wrapperId="selected-products-action-bar-portal">
        {actionBarContentFixed}
      </ReactPortal>
      {actionBarContentStatic}
    </>
  );
}

export default SelectedProductsActionBar;
